import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PayGapCalculator = () => {
  const [payData, setPayData] = useState({
    group1Name: '',
    group1Salary: '',
    group2Name: '',
    group2Salary: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPayData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculatePayGap = () => {
    const { group1Name, group1Salary, group2Name, group2Salary } = payData;

    const group1SalaryNum = parseFloat(group1Salary) || 0;
    const group2SalaryNum = parseFloat(group2Salary) || 0;

    if (!group1Name || !group2Name || group1SalaryNum === 0 || group2SalaryNum === 0) {
      setError('Please enter valid data for both groups');
      return;
    }

    const payGap = group1SalaryNum - group2SalaryNum;
    const payGapPercentage = ((payGap / group1SalaryNum) * 100).toFixed(2);

    const newResults = {
      payGap,
      payGapPercentage
    };

    setResults(newResults);

    setChartData({
      labels: [group1Name, group2Name],
      datasets: [
        {
          label: 'Average Salary',
          data: [group1SalaryNum, group2SalaryNum],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(255, 99, 132, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(255, 99, 132, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const exampleData = {
    group1Name: 'Group A',
    group1Salary: 60000,
    group2Name: 'Group B',
    group2Salary: 50000,
    payGap: 10000,
    payGapPercentage: 16.67
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Pay Gap Calculator | Analyze Salary Differences</title>
        <meta name="description" content="Calculate and visualize the pay gap between different groups with our Pay Gap Calculator." />
        <meta name="keywords" content="Pay Gap Calculator, salary differences, gender pay gap, race pay gap" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">💰</div>
            <h1 className="text-3xl font-bold text-gray-800">Pay Gap Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The Pay Gap Calculator helps you analyze the salary differences between different groups. Enter the names and average salaries of two groups to see the pay gap.</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group 1 Name:</label>
                <input
                  type="text"
                  id="group1Name"
                  name="group1Name"
                  value={payData.group1Name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter Group 1 Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group 1 Average Salary:</label>
                <input
                  type="number"
                  id="group1Salary"
                  name="group1Salary"
                  value={payData.group1Salary}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter Group 1 Average Salary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group 2 Name:</label>
                <input
                  type="text"
                  id="group2Name"
                  name="group2Name"
                  value={payData.group2Name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter Group 2 Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group 2 Average Salary:</label>
                <input
                  type="number"
                  id="group2Salary"
                  name="group2Salary"
                  value={payData.group2Salary}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter Group 2 Average Salary"
                />
              </div>
            </div>

            <button
              onClick={calculatePayGap}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Pay Gap
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Pay Gap Analysis</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Group Details</h3>
                    <p className="text-gray-600">Group 1: {payData.group1Name}</p>
                    <p className="text-gray-600">Group 1 Average Salary: ${payData.group1Salary}</p>
                    <p className="text-gray-600">Group 2: {payData.group2Name}</p>
                    <p className="text-gray-600">Group 2 Average Salary: ${payData.group2Salary}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Pay Gap Results</h3>
                    <p className="text-gray-600">Pay Gap: ${results.payGap}</p>
                    <p className="text-gray-600">Pay Gap Percentage: {results.payGapPercentage}%</p>
                  </div>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Average Salary'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Groups'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Average Salary Comparison',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 mb-6">
                Here's an example calculation using sample data:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4">Sample Pay Data:</h4>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Group 1 Name: {exampleData.group1Name}</li>
                  <li>Group 1 Average Salary: ${exampleData.group1Salary}</li>
                  <li>Group 2 Name: {exampleData.group2Name}</li>
                  <li>Group 2 Average Salary: ${exampleData.group2Salary}</li>
                </ul>
                
                <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
                <ol className="list-decimal pl-8 space-y-4">
                  <li>
                    <p>Pay Gap Calculation:</p>
                    <ul className="list-none pl-8 space-y-2">
                      <li>Pay Gap = Group 1 Salary - Group 2 Salary = ${exampleData.group1Salary} - ${exampleData.group2Salary} = ${exampleData.payGap}</li>
                    </ul>
                  </li>
                  <li>
                    <p>Pay Gap Percentage Calculation:</p>
                    <ul className="list-none pl-8 space-y-2">
                      <li>Pay Gap Percentage = (Pay Gap / Group 1 Salary) * 100 = ({exampleData.payGap} / {exampleData.group1Salary}) * 100 = {exampleData.payGapPercentage}%</li>
                    </ul>
                  </li>
                </ol>
                
                <p className="mt-6 font-semibold">Interpretation:</p>
                <p>In this example, the pay gap between {exampleData.group1Name} and {exampleData.group2Name} is ${exampleData.payGap}, which is a {exampleData.payGapPercentage}% difference.</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Pay Gap <span className="text-blue-600">📊</span></h2>
            <div className="text-gray-600 mb-8">
              <p>A pay gap is the difference in average salaries between different groups. This calculator helps you understand and visualize the pay gap by:</p>
              <ul className="list-disc pl-6 mt-2">
                <li>Comparing the average salaries of two groups</li>
                <li>Showing the pay gap in both absolute and percentage terms</li>
              </ul>
              <p className="italic mt-2">Note: Addressing pay gaps is crucial for promoting fairness and equality in the workplace.</p>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Pay Gap Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to use the Pay Gap Calculator effectively:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter Your Data</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter the name and average salary of Group 1</li>
                  <li>Enter the name and average salary of Group 2</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Calculate Pay Gap</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click "Calculate Pay Gap" to generate the results</li>
                  <li>View the pay gap in both absolute and percentage terms</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Interpret Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Analyze the pay gap and consider actions to address any disparities</li>
                  <li>Use the chart to visualize the salary differences between the two groups</li>
                </ul>
              </div>

              <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Ensure accurate and up-to-date salary data</li>
                  <li>Consider multiple factors that may contribute to pay gaps</li>
                  <li>Use this tool to promote transparency and fairness in compensation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayGapCalculator;